import { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  BarChart3,
  Building,
  ChevronLeft,
  ChevronRight,
  Gauge,
  HandCoins,
  Landmark,
  LayoutDashboard,
  Receipt,
  Settings,
  UserCog,
  Users,
  Wallet,
  type LucideIcon,
} from 'lucide-react';
import { BrandMark } from '@/components/common/BrandMark';
import { EmpresaPicker } from '@/components/common/EmpresaPicker';
import { routes } from '@/lib/routes';
import { cn } from '@/lib/cn';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import type { Empresa } from '@/services/api/empresas';

interface SidebarProps {
  empresaActiva: Empresa;
  empresas: Empresa[];
  canEdit: boolean;
  canCreate: boolean;
  onEdit: () => void;
  onCreate: () => void;
  collapsed: boolean;
  onToggleCollapsed: () => void;
}

type PulseMenuKey =
  | 'clientes'
  | 'control_abonos'
  | 'finanzas'
  | 'facturacion'
  | 'conciliacion'
  | 'reportes'
  | 'analytics'
  | 'configuracion';

interface NavItem {
  label: string;
  to: string;
  icon: LucideIcon;
  /** Qué permiso PULSE hace falta para verlo. Si no se indica, todos los roles operativos lo ven. */
  pulseMenu?: PulseMenuKey;
  /** Si true, sólo APEX/PARTNER lo ven. */
  adminOnly?: boolean;
  /** Si true, el rol ADMINISTRADOR también lo ve. Default: false (oculto). */
  visibleToAdministrador?: boolean;
}

export function Sidebar({
  empresaActiva,
  empresas,
  canEdit,
  canCreate,
  onEdit,
  onCreate,
  collapsed,
  onToggleCollapsed,
}: SidebarProps) {
  const { isApex, isPartner, isPulse, isAdministrador, profile } = useAuth();
  const empresaId = empresaActiva.id;

  // Cargar permisos PULSE si aplica
  const [pulsePerms, setPulsePerms] = useState<Set<PulseMenuKey> | null>(
    isPulse ? null : new Set(),
  );
  useEffect(() => {
    if (!isPulse || !profile?.id) {
      setPulsePerms(new Set());
      return;
    }
    void (async () => {
      const { data } = await supabase
        .from('pulse_permissions')
        .select('menu, can_view')
        .eq('profile_id', profile.id);
      const s = new Set<PulseMenuKey>();
      (data ?? []).forEach((p: { menu: string; can_view: boolean }) => {
        if (p.can_view) s.add(p.menu as PulseMenuKey);
      });
      setPulsePerms(s);
    })();
  }, [isPulse, profile?.id]);

  const items: NavItem[] = [
    {
      label: 'Dashboard',
      to: routes.empresa.dashboard(empresaId),
      icon: LayoutDashboard,
      visibleToAdministrador: true,
    },
    {
      label: 'Administraciones',
      to: routes.empresa.clientes.administraciones(empresaId),
      icon: Users,
      pulseMenu: 'clientes',
    },
    {
      label: 'Edificios',
      to: routes.empresa.clientes.edificios(empresaId),
      icon: Building,
      pulseMenu: 'clientes',
    },
    {
      label: 'Control de abonos',
      to: routes.empresa.controlAbonos(empresaId),
      icon: Gauge,
      pulseMenu: 'control_abonos',
    },
    {
      label: 'Finanzas',
      to: routes.empresa.finanzas(empresaId),
      icon: Wallet,
      pulseMenu: 'finanzas',
    },
    {
      label: 'Facturación',
      to: routes.empresa.facturacion(empresaId),
      icon: Receipt,
      pulseMenu: 'facturacion',
      visibleToAdministrador: true,
    },
    {
      label: 'Recupero',
      to: routes.empresa.recupero(empresaId),
      icon: HandCoins,
      // TODO-MDC-17: re-usamos pulse_can_view('facturacion') por decisión
      // del usuario (no permisos separados de recupero).
      pulseMenu: 'facturacion',
    },
    {
      label: 'Conciliación',
      to: routes.empresa.conciliacion(empresaId),
      icon: Landmark,
      pulseMenu: 'conciliacion',
    },
    {
      label: 'Reportes',
      to: routes.empresa.reportes(empresaId),
      icon: BarChart3,
      pulseMenu: 'reportes',
    },
    {
      label: 'Usuarios',
      to: routes.empresa.usuarios(empresaId),
      icon: UserCog,
      adminOnly: true,
    },
    {
      label: 'Configuración',
      to: routes.empresa.configuracion(empresaId),
      icon: Settings,
      pulseMenu: 'configuracion',
    },
  ];

  // Filtrar según rol
  const visibleItems = items.filter((it) => {
    if (it.adminOnly) return isApex || isPartner;
    if (isApex || isPartner) return true;
    if (isAdministrador) return it.visibleToAdministrador === true;
    if (isPulse) {
      if (!it.pulseMenu) return true;
      return pulsePerms?.has(it.pulseMenu) ?? false;
    }
    return false;
  });

  return (
    <aside
      className={cn(
        'relative flex h-screen flex-col border-r border-surface-line bg-white/70 backdrop-blur-sm',
        'transition-[width] duration-200 ease-out',
        collapsed ? 'w-20' : 'w-64',
      )}
    >
      {/* Brand */}
      <div
        className={cn(
          'flex items-center overflow-hidden pb-4 pt-5',
          collapsed ? 'justify-center px-2' : 'pl-8 pr-5',
        )}
      >
        <BrandMark variant={collapsed ? 'isotipo' : 'full'} size={collapsed ? 38 : 56} />
      </div>

      {/* Empresa picker */}
      <div className={cn('mb-3', collapsed ? 'flex justify-center' : 'mx-3')}>
        <EmpresaPicker
          variant="sidebar"
          collapsed={collapsed}
          empresaActiva={empresaActiva}
          empresas={empresas}
          canEdit={canEdit}
          canCreate={canCreate}
          onEdit={onEdit}
          onCreate={onCreate}
        />
      </div>

      <nav
        className={cn(
          'flex-1 overflow-y-auto overflow-x-hidden py-2',
          collapsed ? 'px-2' : 'px-3',
        )}
      >
        {visibleItems.map((item) => (
          <SidebarItem
            key={item.to}
            label={item.label}
            to={item.to}
            icon={item.icon}
            collapsed={collapsed}
          />
        ))}
      </nav>

      {/* Footer tagline: solo visible expandido */}
      <div
        className={cn(
          'border-t border-surface-line py-3 transition-opacity',
          collapsed ? 'px-2 opacity-0' : 'px-5 opacity-100',
        )}
      >
        <p className="text-[10px] font-semibold uppercase tracking-widest text-neutral-muted">
          Tu gestión. Todo en orden.
        </p>
      </div>

      {/* Botón toggle flotante sobre el borde derecho */}
      <button
        type="button"
        onClick={onToggleCollapsed}
        aria-label={collapsed ? 'Expandir barra lateral' : 'Colapsar barra lateral'}
        title={collapsed ? 'Expandir' : 'Colapsar'}
        className={cn(
          'absolute top-20 grid h-6 w-6 place-items-center rounded-full border border-surface-line bg-white text-neutral-muted shadow-premium-soft transition-all',
          'hover:border-blue/40 hover:text-blue hover:shadow-premium',
          '-right-3',
        )}
      >
        {collapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
      </button>
    </aside>
  );
}

function SidebarItem({
  label,
  to,
  icon: Icon,
  collapsed,
}: {
  label: string;
  to: string;
  icon: LucideIcon;
  collapsed: boolean;
}) {
  return (
    <NavLink
      to={to}
      title={collapsed ? label : undefined}
      className={({ isActive }) =>
        cn(
          'my-0.5 flex items-center rounded-md text-sm font-medium transition',
          collapsed ? 'justify-center px-0 py-2.5' : 'gap-3 px-3 py-2.5',
          isActive
            ? 'bg-blue-10 text-blue'
            : 'text-neutral-ink/80 hover:bg-blue-10/60 hover:text-neutral-ink',
        )
      }
    >
      <Icon size={18} strokeWidth={2} className="flex-none" />
      {!collapsed && <span className="truncate">{label}</span>}
    </NavLink>
  );
}
